import { DateTime } from 'luxon';
import type { FC } from 'react';
import { Suspense, useEffect, useState } from 'react';
import { RouterProvider } from 'react-router-dom';
import { MiddleSpinner } from './components/middle-spinner/index.js';
import {
  CacheProvider,
  defineFetchAllCacheFor,
  defineFetchOneCacheFor,
} from './hooks/use-cache.js';
import { useRouter } from './router.js';
import { AuthStatus, useAuth } from './utils/auth/index.js';

const fetchAllCaches = {
  organization: defineFetchAllCacheFor('/api/v1/organizations'),
  person: defineFetchAllCacheFor('/api/v1/persons?limit=999'),
};

const fetchOneCaches = {
  patrol: defineFetchOneCacheFor('/api/v1/patrols'),
};

const App: FC = () => {
  const auth = useAuth();
  const [refreshExpired, setRefreshExpired] = useState(false);

  useEffect(() => {
    const state = auth.state;
    if (state?.status !== AuthStatus.AUTHENTICATED || !state.refreshExpires) {
      setRefreshExpired(false);
      return;
    }

    const refreshExpiresIn = state.refreshExpires - DateTime.now().toMillis();

    if (refreshExpiresIn < 0) {
      console.log('Refresh token already expired');
      setRefreshExpired(true);
      return;
    }
    setRefreshExpired(false);
    if (refreshExpiresIn > 2 ** 31) return;

    const handle = setTimeout(() => {
      console.log('Refresh token expired');
      setRefreshExpired(true);
    }, refreshExpiresIn);

    return () => clearTimeout(handle);
  }, [auth]);

  const router = useRouter();

  if (!auth.state) {
    return <MiddleSpinner />;
  }

  if (auth.state.status !== AuthStatus.AUTHENTICATED || refreshExpired) {
    return (
      <Suspense fallback={<MiddleSpinner />}>
        <RouterProvider fallbackElement={<MiddleSpinner />} router={router} />
      </Suspense>
    );
  }

  return (
    <CacheProvider
      authState={auth.state}
      fetchAllCaches={fetchAllCaches}
      fetchOneCaches={fetchOneCaches}
    >
      <Suspense fallback={<MiddleSpinner />}>
        <RouterProvider fallbackElement={<MiddleSpinner />} router={router} />
      </Suspense>
    </CacheProvider>
  );
};

export default App;
